/**
 * Z-index constants for the application.
 * Based on MUI's default z-index scale with custom additions.
 *
 * MUI defaults:
 * - mobileStepper: 1000
 * - fab: 1050
 * - speedDial: 1050
 * - appBar: 1100
 * - drawer: 1200
 * - modal: 1300
 * - snackbar: 1400
 * - tooltip: 1500
 */
/**
 * The layer every `MobileDrawer` renders on. Derive anything that has to clear a
 * mobile sheet from THIS, never from a literal — a hardcoded number can't follow
 * when this moves, which is exactly how the Libraries preview card ended up
 * pinned at 1201 (correct against MUI's old 1200 drawer layer) long after
 * MobileDrawer had moved to 1400.
 */
const MOBILE_DRAWER = 1400;

export const Z_INDEX = {
  // MUI's default drawer layer — below modals. Used by a drawer that launches modals
  // meant to appear ABOVE it (e.g. the mobile Add Media drawer), the reverse of the
  // usual "drawer overlays its launcher" case (MobileDrawer's default 1400).
  drawer: 1200,
  modal: 1300,
  /**
   * The toast layer — deliberately sandwiched between `modal` and the 1400 rungs.
   *
   * It used to be 1250, below modals, so that "MUI Menu/Modal/Dialog (1300+) sit above
   * them" (66195b76). Half of that was right and half of it hid the toasts that matter most:
   * the media preview is a modal, and copy-link / download / rename / retry / delete all
   * fire their only feedback from inside it. A toast the user cannot see is the same as no
   * toast, and worse than one overlapping a modal's bottom edge — so toasts now clear
   * modals.
   *
   * Still below 1400, which keeps the half that was right: an open dropdown (`BaseMenu`,
   * 1400) and a `MobileDrawer` (1400) both stay above the toast stack. A toast must not
   * cover the menu the user is reading, and mobile toasts are top-anchored while sheets are
   * bottom-anchored, so nothing is lost there either.
   */
  toast: 1350,
  snackbar: 1400,
  // The base layer for MobileDrawer itself. Imported by MobileDrawer.tsx.
  mobileDrawer: MOBILE_DRAWER,
  // One rung above a MobileDrawer. Two distinct uses, both "clear the sheet below me":
  //  - a modal launched from ON TOP of a sheet (EditWordsModal, from the mobile
  //    create-library drawer)
  //  - a SECOND sheet stacked on a first (MediaDeleteModal, FailedMediaListModal —
  //    these pass this value into MobileDrawer's own `zIndex` prop)
  // Still below `tooltip` so hover/press tooltips show over it.
  //
  // ON ADDING MORE RUNGS: don't hand-maintain aboveMobileDrawer2 / 3 / … names.
  // Only ~99 units separate MOBILE_DRAWER from `tooltip`, so rungs must be single
  // increments — a second `+50` would land rung 2 exactly on top of tooltips.
  // Rank-based names also carry no meaning at the call site: "2" doesn't tell the
  // next reader why it should beat "1". When a third layer is genuinely needed
  // (two sheets stacked on a base sheet, all three visible at once), add a helper
  // instead so the tooltip ceiling is asserted in ONE place:
  //   export const mobileDrawerLayer = (n: number) => MOBILE_DRAWER + n;
  // and have it throw (or clamp) at `n >= tooltip - MOBILE_DRAWER`.
  // Don't do this speculatively — as of writing, every consumer is "one layer above
  // one drawer" and no two of them are ever on screen together.
  aboveMobileDrawer: MOBILE_DRAWER + 50,
  tooltip: 1500,
  // Drag-drop overlay must be above modals, but allows choose-file-upload modal
  dragDropOverlay: 10000,
} as const;
